import { useParams, useNavigate, Link } from "react-router-dom";
import { defaultGetFetcher, getDjangoAppUrl, handleAxiosError, IErrorResponse } from "../../../api/base";
import { z } from "zod";
import { Card } from "../../../components/card";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import useSWRImmutable from "swr/immutable";
import { camelCaseToNormal } from "../../../util/string";

//#region zod schemas

const LeadRequestDetailsDtoSchema = z.object({
  id: z.number(),
  created: z.date(),
  email: z.string().optional(),
  mixpanelAnonUserId: z.string(),
  status: z.string(),
  sourceType: z.string(),
  rangeOfAssets: z.string().optional(),
  requestedState: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  leadInfo: z.string(),
  phoneNumber: z.string(),
  utmData: z.record(z.string(), z.unknown()),
  notes: z.string(),
  googleClientId: z.string(),
});

const MatchedAdvisorDtoSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().optional(),
  explanation: z.string().optional(),
  signUpDate: z.string(),
  legalConfirmationDate: z.string().optional(),
  additionalProfileInformation: z.record(z.string(), z.unknown()),
  gender: z.string().optional(),
  bio: z.string().optional(),
  certifications: z.array(z.string()),
});

const SupabaseConversationEntrySchema = z.object({
  log_id: z.number(),
  ai_msg: z.string(),
  user_msg: z.string(),
  is_predefined_ai_question: z.boolean().default(false),
});

const AIMessageSchema = z.object({
  id: z.number(),
  message: z.string(),
  sentOn: z.string(),
  isAiResponse: z.boolean(),
  messageAuthorId: z.number().nullable(),
  messageAuthorName: z.string().nullable(),
});

const LeadRequestConversationResultSchema = z.object({
  leadRequestDetails: LeadRequestDetailsDtoSchema,
  cachedAdvisorAiResponse: z.array(MatchedAdvisorDtoSchema),
  conversationLog: z.array(SupabaseConversationEntrySchema),
  additionalAiMessages: z.array(AIMessageSchema),
});

type LeadRequestDetailsDto = z.infer<typeof LeadRequestDetailsDtoSchema>;
type MatchedAdvisorDto = z.infer<typeof MatchedAdvisorDtoSchema>;
type SupabaseConversationEntryDto = z.infer<typeof SupabaseConversationEntrySchema>;

type LeadRequestConversationResult = z.infer<typeof LeadRequestConversationResultSchema>;
//#endregion

export const LeadRequestDetailsPage = () => {
  const { leadRequestId } = useParams();
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    error: loadingError,
  } = useSWRImmutable<LeadRequestConversationResult, IErrorResponse>(
    `api/v2/admin/advertising/lead/${leadRequestId}/ai-summary`,
    (url: string) => defaultGetFetcher(url),
  );

  const onCancel = () => {
    navigate(`/lead-requests`);
  };

  if (isLoading) {
    return (
      <div className="lg:h-full overflow-y-auto">
        <header>
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">Lead request details</h1>
        </header>
        <div className="mt-4">
          <Card className="w-full h-full px-4 mb-2 border border-gray-100">
            <ul role="list" className="divide-y divide-gray-100">
              <li className="flex flex-col gap-y-2 py-5">
                <div className="flex items-start gap-x-2">
                  <p className="text-sm font-semibold leading-6 text-gray-900/80">Loading...</p>
                </div>
                <div className="w-full h-3 bg-gray-200/80 animate-pulse rounded-lg"></div>
                <div className="w-1/4 h-3 bg-gray-200/80 animate-pulse rounded-lg"></div>
                <div className="w-3/4 h-3 bg-gray-200/80 animate-pulse rounded-lg"></div>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    );
  }

  if (loadingError !== undefined || data === undefined) {
    let handledError: IErrorResponse;
    if (loadingError) handledError = handleAxiosError(loadingError);
    else
      handledError = {
        error: "Data is undefined",
        errorDetails: "Some unexpected error occurred. Please try again later or contact support.",
      };

    return (
      <div className="lg:h-full overflow-y-auto">
        <header>
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">Lead request details</h1>
        </header>
        <div className="mt-4">
          <Card className="w-full h-full mb-2 border border-gray-100 flex flex-col divide-y-0">
            <div className="flex flex-col gap-y-4">
              <p className="font-semibold leading-6 text-gray-900/80">Could not load request</p>

              <div className="w-full text-sm">
                <p>{handledError.error}</p>
                <p>{handledError.errorDetails}</p>
              </div>
              <div className="w-full flex justify-start">
                <button
                  onClick={onCancel}
                  className="text-gray-900 border border-gray-300 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center "
                >
                  Go back
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const getLeadInformation = (data: LeadRequestDetailsDto) => {
    const fields = [
      { label: "Full name", value: `${data.firstName} ${data.lastName}` },
      { label: "Email address", value: data.email },
      { label: "Mixpanel Anon User ID", value: data.mixpanelAnonUserId },
      { label: "Status", value: data.status },
      { label: "Source Type", value: data.sourceType },
      { label: "Range of Assets", value: data.rangeOfAssets },
      { label: "Requested state", value: data.requestedState },
      { label: "Lead Info", value: data.leadInfo },
      { label: "Phone Number", value: data.phoneNumber },
      { label: "UTM Data", value: JSON.stringify(data.utmData) },
      { label: "Notes", value: data.notes },
      { label: "Google Client ID", value: data.googleClientId },
    ];

    return (
      <div>
        <div className="px-4 sm:px-0">
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Personal details and application.</p>
        </div>
        <div className="mt-6 border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            {fields.map((field, index) => (
              <div key={index} className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-xs font-medium leading-6 text-gray-900">{field.label}</dt>
                <dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{field.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    );
  };

  const getAdvisorAiSummary = (data: MatchedAdvisorDto[]) => {
    return (
      <div>
        <div className="px-4 sm:px-0">
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Personal details and application.</p>
        </div>
        <div className="mt-6 divide-y-2 border-t-2 border-primary-200 divide-primary-200 flex flex-col gap-y-2">
          {data.length === 0 && (
            <div className="flex w-full h-full items-center justify-center text-gray-400 text-sm mt-2">
              <p className="font-medium text-gray-900">
                User hasn't completed survey. No advisors were found for this lead request.
              </p>
            </div>
          )}
          {data.map((advisor, _) => (
            <dl className="divide-y divide-gray-100" key={advisor.id}>
              <div key="advisorId" className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-xs font-medium leading-6 text-gray-900">Advisor</dt>
                <dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {advisor.firstName + " " + advisor.lastName} ({advisor.id})
                </dd>
              </div>
              {Object.entries(advisor)
                .filter((key) => !["id", "firstName", "lastName", "additionalProfileInformation"].includes(key[0]))
                .map((key, _) => {
                  let value = "N/A";
                  if (key[1] === null) value = "N/A";
                  else if (key[0] === "certifications") {
                    value = (key[1] as string[]).join(", ");
                  } else if (key[0] === "additionalProfileInformation") {
                    value = JSON.stringify(key[1], null, 2);
                  } else {
                    value = String(key[1]);
                  }

                  return (
                    <div
                      key={`${advisor.id} - ${key[0]}`}
                      className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
                    >
                      <dt className="text-xs font-medium leading-6 text-gray-900">{camelCaseToNormal(key[0])}</dt>
                      <dd className="mt-1 text-xs leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{value}</dd>
                    </div>
                  );
                })}
            </dl>
          ))}
        </div>
      </div>
    );
  };

  const getLeadConversationLog = (data: SupabaseConversationEntryDto[], sourceType: string) => {
    return (
      <div>
        <div className="px-4 sm:px-0">
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
            {sourceType === "AI" ? "Conversation log with AI." : "Read lead survey answers"}
          </p>
        </div>
        <div className="mt-6 border-t-2 border-primary-200 divide-primary-200">
          <ul role="list">
            {data.length === 0 && (
              <li>
                <div className="flex w-full h-full items-center justify-center text-gray-400 text-sm mt-2">
                  <p className="font-medium text-gray-900">User hasn't answered any questions.</p>
                </div>
              </li>
            )}
            {data.map((entry, index) => (
              <li key={index}>
                <div className="relative pb-4 flex flex-row">
                  {index !== data.length - 1 ? (
                    // Vertical column line
                    <span aria-hidden="true" className="absolute left-3 top-4 -ml-px h-full w-0.5 bg-gray-200" />
                  ) : null}
                  <>
                    <div className="relative flex h-6 w-6 pt-1 flex-none items-center justify-center bg-white">
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300" />
                    </div>
                    <div className="flex-auto flex-col py-0.5 pt-1 text-xs leading-5 text-gray-500">
                      <p className="">
                        <span className="font-medium text-gray-900">Question:</span> {entry.ai_msg}
                      </p>
                      <p className="">
                        <span className="font-medium text-gray-900">Answer:</span> {entry.user_msg}
                      </p>
                    </div>
                  </>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderDisclosureContent = (title: string, open: boolean, content: JSX.Element) => {
    return (
      <>
        <Disclosure.Button
          className={clsx(
            "flex w-full justify-between items-center my-2 py-2 px-4 text-left rounded-lg bg-primary-50/75 hover:bg-primary-100 focus:outline-none focus-visible:ring focus-visible:ring-primary-500/75",
            { "bg-primary-100": open },
          )}
        >
          <h3 className="text-base font-semibold leading-7 text-gray-900">{title}</h3>
          <ChevronRightIcon className={clsx("h-5 w-5 text-primary-500", { "rotate-90 transform": open })} />
        </Disclosure.Button>
        <Transition
          show={open}
          className="transition-all duration-500 overflow-hidden"
          enterFrom="transform scale-95 opacity-0 h-0"
          enterTo="transform scale-100 opacity-100"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0 max-h-0"
        >
          <Disclosure.Panel className="text-gray-500 mt-2">
            <div className="flex flex-col w-full gap-y-6 mb-6 px-4">{content}</div>
          </Disclosure.Panel>
        </Transition>
      </>
    );
  };

  return (
    <div className="lg:h-full overflow-y-auto">
      <header>
        <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">Lead request details</h1>
      </header>
      <div className="mt-4">
        <Card className="w-full h-full mb-2 border border-gray-100 flex flex-col divide-y-0">
          <Disclosure defaultOpen>
            {({ open }) =>
              renderDisclosureContent("Applicant Information", open, getLeadInformation(data.leadRequestDetails))
            }
          </Disclosure>
          <Disclosure>
            {({ open }) =>
              renderDisclosureContent(
                "Conversation log",
                open,
                getLeadConversationLog(data.conversationLog, data.leadRequestDetails.sourceType),
              )
            }
          </Disclosure>
          <Disclosure>
            {({ open }) =>
              renderDisclosureContent("AI Summary", open, getAdvisorAiSummary(data.cachedAdvisorAiResponse))
            }
          </Disclosure>
          <div className="flex justify-between px-4 py-2">
            <button
              onClick={onCancel}
              className="text-gray-900 border border-gray-300 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center "
            >
              Cancel
            </button>
            <span className="flex flex-row gap-2">
              <Link
                rel="noopener noreferrer"
                target="_blank"
                to={getDjangoAppUrl(
                  `admin/advertising/leadrequestforsessionwithadvisor_liteadminproxy/${data.leadRequestDetails.id}/change/`,
                )}
                className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center
              disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:bg-gray-300 disabled:cursor-not-allowed"
              >
                View in Django
              </Link>
              <Link
                to={`/lead-requests/${leadRequestId}/rematch`}
                className="
              text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center
              "
              >
                AI Rematch
              </Link>
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
};
